import { Button, Card, Combobox, Form, Input } from '@ui/themed';
import { t } from 'i18next';
import { useMemo } from 'react';
import { regions } from '../../../constants/regions';
import { useCountries } from '../../../hooks/use-countries';
import type { CountryCode } from '../../../utils/countries-iso3166';
import { stringToSlug } from '../../../utils/slug';
import { useCustomerGroups } from '../../customer-groups';
import { useShippingMethodForm } from '../builder-context';
import { ShippingBandBuilder } from '../components/shipping-band-builder';
import { MAX_NAME_LENGTH } from '../schemas';

export function ShippingMethodBuilder({
  onCancel,
  isEditing,
}: {
  onCancel: () => Promise<void> | void;
  isEditing?: boolean;
}) {
  const form = useShippingMethodForm();

  const { data: customerGroups } = useCustomerGroups();
  const countries = useCountries();
  const countryOptions = useMemo(
    () => countries.map(({ code, name }) => ({ value: code, label: name })),
    [countries],
  );

  // For each country selected, have a corresponding array for the country in the regions list (even
  // if no regions - set this to an empty array). If a country is removed, its region array is also
  // removed
  function syncRegionsToCountries(selectedCountryCodes: CountryCode[]) {
    const newRegions: Partial<Record<CountryCode, string[]>> = {};
    for (const code of selectedCountryCodes) {
      newRegions[code] = form.state.values.regions[code] || [];
    }

    return newRegions;
  }

  return (
    <Form
      onSubmit={async () => {
        // Clear errors so we can re-validate
        form.setErrorMap({ onSubmit: { fields: {} } });

        await form.handleSubmit();
      }}
    >
      {/* Shipping method details e.g. name and sku */}
      <Card>
        <Card.Header headingLevel="h2">
          {t('features:shipping_methods.components.shipping_method_builder.details.title')}
        </Card.Header>
        <Card.Body>
          <fieldset className="flex w-full flex-col gap-2 sm:flex-row">
            {/* Shipping method name */}
            <span className="flex-1">
              <form.Field
                name="name"
                listeners={{
                  onChange: ({ value, fieldApi }) => {
                    fieldApi.form.setFieldValue('sku', stringToSlug(value));
                  },
                }}
              >
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={field.handleChange}
                    error={field.state.meta.errors[0]?.message}
                    maxLength={MAX_NAME_LENGTH}
                    label={t(
                      'features:shipping_methods.components.shipping_method_builder.details.name.label',
                    )}
                    placeholder={t(
                      'features:shipping_methods.components.shipping_method_builder.details.name.placeholder',
                    )}
                    description={t(
                      'features:shipping_methods.components.shipping_method_builder.details.name.description',
                    )}
                  />
                )}
              </form.Field>
            </span>

            {/* Shipping method sku */}
            <span className="flex-1">
              <form.Field name="sku">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={field.handleChange}
                    error={field.state.meta.errors[0]?.message}
                    maxLength={MAX_NAME_LENGTH}
                    label={t(
                      'features:shipping_methods.components.shipping_method_builder.details.sku.label',
                    )}
                    placeholder={t(
                      'features:shipping_methods.components.shipping_method_builder.details.sku.placeholder',
                    )}
                    description={t(
                      'features:shipping_methods.components.shipping_method_builder.details.sku.description',
                    )}
                  />
                )}
              </form.Field>
            </span>
          </fieldset>

          {/* Shipping method priority - which order it shows at checkout */}
          <form.Field name="priority">
            {(field) => (
              <Input
                name="priority"
                onChange={field.handleChange}
                error={field.state.meta.errors[0]?.message}
                value={field.state.value}
                inputMode="numeric"
                label={t(
                  'features:shipping_methods.components.shipping_method_builder.details.priority.label',
                )}
                placeholder={t(
                  'features:shipping_methods.components.shipping_method_builder.details.priority.placeholder',
                )}
                description={t(
                  'features:shipping_methods.components.shipping_method_builder.details.priority.description',
                )}
              />
            )}
          </form.Field>

          {/* Groups the method applies to */}
          <form.Field name="customerGroups" mode="array">
            {(field) => (
              <Combobox
                label={t(
                  'features:shipping_methods.components.shipping_method_builder.details.customer_groups.label',
                )}
                placeholder={t(
                  'features:shipping_methods.components.shipping_method_builder.details.customer_groups.placeholder',
                )}
                noOptionsMessage={t(
                  'features:shipping_methods.components.shipping_method_builder.details.customer_groups.no_options',
                )}
                tooltip={t(
                  'features:shipping_methods.components.shipping_method_builder.details.customer_groups.tooltip',
                )}
                options={customerGroups.map((g) => ({ value: g.slug, label: g.name }))}
                value={field.state.value}
                onChange={field.handleChange}
                error={field.state.meta.errors[0]?.message}
                multiple
              />
            )}
          </form.Field>
        </Card.Body>
      </Card>

      {/* Regions the shipping method applies to */}
      <Card>
        <Card.Header headingLevel="h2">
          {t('features:shipping_methods.components.shipping_method_builder.regions.title')}
        </Card.Header>
        <Card.Body>
          <form.Field
            name="countries"
            mode="array"
            listeners={{
              onChange: ({ value, fieldApi }) => {
                fieldApi.form.setFieldValue(
                  'regions',
                  syncRegionsToCountries(value as CountryCode[]),
                );
              },
            }}
          >
            {(field) => (
              <Combobox
                label={t(
                  'features:shipping_methods.components.shipping_method_builder.regions.countries.label',
                )}
                placeholder={t(
                  'features:shipping_methods.components.shipping_method_builder.regions.countries.placeholder',
                )}
                noOptionsMessage={t(
                  'features:shipping_methods.components.shipping_method_builder.regions.countries.no_options',
                )}
                options={countryOptions}
                value={field.state.value}
                onChange={field.handleChange}
                error={field.state.meta.errors[0]?.message}
                multiple
              />
            )}
          </form.Field>

          {/* Show a region selector for every selected country that we have defined regions for */}
          <form.Subscribe selector={(state) => state.values.countries}>
            {(countries) =>
              countries // For all countries selected by user
                .filter((c) => c in regions) // Keep only countries that have regions defined
                .map((c) => (
                  <form.Field key={c} name={`regions.${c as CountryCode}`}>
                    {(field) => (
                      <Combobox
                        label={t(
                          'features:shipping_methods.components.shipping_method_builder.regions.regions.label',
                          { country: c },
                        )}
                        placeholder={t(
                          'features:shipping_methods.components.shipping_method_builder.regions.regions.placeholder',
                        )}
                        noOptionsMessage={t(
                          'features:shipping_methods.components.shipping_method_builder.regions.regions.no_options',
                        )}
                        value={field.state.value ?? []}
                        onChange={field.handleChange}
                        error={field.state.meta.errors[0]?.message}
                        options={Object.entries(regions[c as keyof typeof regions]).map(
                          ([code, name]) => ({
                            value: code,
                            label: name as string,
                          }),
                        )}
                        multiple
                      />
                    )}
                  </form.Field>
                ))
            }
          </form.Subscribe>
        </Card.Body>
      </Card>

      {/* Shipping bands form - user can add many bands */}
      <ShippingBandBuilder isEditing={isEditing} />

      <span className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          {t('buttons:cancel')}
        </Button>
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Button type="submit" loading={isSubmitting}>
              {t(
                `features:shipping_methods.components.shipping_method_builder.actions.${isEditing ? 'update' : 'create'}`,
              )}
            </Button>
          )}
        </form.Subscribe>
      </span>
    </Form>
  );
}
